import React, { useState, useEffect, useCallback } from "react";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  deleteDoc,
  doc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";

// Configuración de Firebase
const firestore = getFirestore();
const storage = getStorage();

const FileUpload = ({
  taskId,
  onUpload,
  onDeleteAttachment,
  existingAttachments = [],
}) => {
  const [fileInfo, setFileInfo] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [fileCount, setFileCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Cargar archivos existentes para la tarea actual
  useEffect(() => {
    const loadFiles = async () => {
      try {
        if (taskId) {
          const q = query(
            collection(firestore, "files"),
            where("taskId", "==", taskId)
          );
          const querySnapshot = await getDocs(q);
          const files = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setFileInfo(files);
          setFileCount(files.length);
        }
      } catch (error) {
        console.error("Error al cargar archivos:", error);
      } finally {
        setLoading(false);
      }
    };

    loadFiles();
  }, [taskId]);

  // Función para subir archivo a Firebase Storage
  const uploadFileToStorage = async (file) => {
    const storageRef = ref(storage, "carpeta_archivos/" + file.name);
    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(prog);
        },
        (error) => {
          console.error("Error al subir el archivo:", error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            console.error("Error al obtener la URL de descarga:", error);
            reject(error);
          }
        }
      );
    });
  };

  // Función para manejar la carga del archivo
  const handleUpload = useCallback(
    async (file) => {
      if (!file) return;
      setUploading(true);
      try {
        const fileURL = await uploadFileToStorage(file);
        const fileName = file.name;
        // Guardar la información del archivo en Firestore
        const docRef = await addDoc(collection(firestore, "files"), {
          url: fileURL,
          name: fileName,
          taskId: taskId,
          createdAt: serverTimestamp(),
        });
        const newFile = { id: docRef.id, url: fileURL, name: fileName };
        // Actualización funcional del estado para fileInfo y fileCount
        setFileInfo((prevFiles) => {
          const updated = [...prevFiles, newFile];
          setFileCount(updated.length);
          return updated;
        });
        // Notificar a la tarea (callback)
        onUpload(taskId, [newFile]);
        setProgress(0);
        setUploading(false);
      } catch (error) {
        console.error("Error al subir el archivo:", error);
        setError("Error al subir el archivo. Inténtelo de nuevo.");
        setUploading(false);
        setProgress(0);
      }
    },
    [taskId, onUpload]
  );

  const onDrop = (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    // 1) Verificar duplicados
    const duplicates = acceptedFiles.filter((file) =>
      existingAttachments.some((att) => att.name === file.name)
    );

    // 2) Avisar si hay duplicados
    if (duplicates.length > 0) {
      Swal.fire(
        "Archivo duplicado",
        `Se omitió: ${duplicates.map((f) => f.name).join(", ")}`,
        "warning"
      );
    }

    // 3) Filtrar los NO duplicados
    const nonDuplicates = acceptedFiles.filter(
      (file) => !existingAttachments.some((att) => att.name === file.name)
    );

    // 4) Subir solo los no duplicados
    nonDuplicates.forEach((file) => {
      // si el tamaño está OK
      if (file.size <= 5 * 1024 * 1024) {
        handleUpload(file);
      } else {
        setError("El archivo excede el tamaño máximo permitido (5MB)");
      }
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleDownload = (fileURL, fileName) => {
    const link = document.createElement("a");
    link.href = fileURL;
    link.setAttribute("download", fileName);
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (fileId) => {
    try {
      await deleteDoc(doc(firestore, "files", fileId));
      setFileInfo((prevFiles) => {
        const updated = prevFiles.filter((file) => file.id !== fileId);
        setFileCount(updated.length);
        return updated;
      });
      onDeleteAttachment(taskId, fileId);
    } catch (error) {
      console.error("Error al eliminar el archivo:", error);
      setError("Error al eliminar el archivo. Inténtelo de nuevo.");
    }
  };

  return (
    <div style={{ maxWidth: "600px", margin: "auto" }}>
      <h4 style={{ fontWeight: "400", color: "#757575" }}>Cargar un archivo</h4>
      <div {...getRootProps()} className="dropzone">
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Suelta el archivo aquí...</p>
        ) : (
          <>
            <p>
              Arrastra y suelta un archivo aquí, o haz clic para seleccionar un
              archivo
            </p>
            <p style={{ color: "#007bff" }}>(Max. File size: 5MB)</p>
          </>
        )}
      </div>
      {uploading && (
        <div className="progress-container">
          <progress value={progress} max="100" />
          <span style={{ marginLeft: "10px" }}>{`${Math.round(
            progress
          )}%`}</span>
        </div>
      )}
      {loading ? (
        <p>Cargando...</p>
      ) : fileCount === 0 ? (
        <p>No hay archivos</p>
      ) : (
        fileInfo.map((file) => (
          <div key={file.id} className="file-info">
            <div className="file-container">
              <div className="file-image">
                <img src="/img/file.svg" alt="Archivo Subido" />
              </div>
              <div className="file-content">
                <div className="file-comment">
                  <p style={{ fontSize: "14px", marginBottom: "20px" }}>
                    Archivo: {file.name}
                  </p>
                </div>
                <div className="file-actions">
                  <button onClick={() => handleDownload(file.url, file.name)}>
                    Descargar
                  </button>
                  <button onClick={() => handleDelete(file.id)}>
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
      {error && <p>{error}</p>}
    </div>
  );
};

export default FileUpload;
